import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { apiService } from '../../services/api';

interface RenewalSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: {
    id: string;
    username: string;
    password?: string;
    email?: string;
    whatsapp?: string;
    contact?: string;
    max_connections?: number;
    maxConnections?: number;
    is_trial?: number | boolean;
    exp_date?: string | number;
    system?: number;
    package_name?: string;
    packageName?: string;
  };
  renewalData: {
    new_exp_date: number;
    new_exp_date_formatted: string;
    cost_paid: number;
  };
}

const RenewalSuccessModal: React.FC<RenewalSuccessModalProps> = ({
  isOpen,
  onClose,
  client,
  renewalData
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [formattedMessage, setFormattedMessage] = useState('');
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
      fetchAndFormatMessage();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
    // Reformatar quando abrir ou quando a senha/usuário chegarem (evita {password} vazio)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client?.password, client?.username, renewalData?.new_exp_date_formatted]);

  /** Negrito no WhatsApp (*texto*). Remove * internos para não quebrar a formatação. */
  const whatsappBold = (value: string | undefined | null): string => {
    const v = (value ?? '').toString();
    if (!v) return '';
    const safe = v.replace(/\*/g, '');
    return `*${safe}*`;
  };

  /**
   * Credenciais só em texto (negrito WhatsApp), sem links wa.me — copiar/colar fica limpo e igual ao envio da API.
   */
  const whatsappCredentialBlock = (value: string | undefined | null): string => {
    return whatsappBold(value);
  };

  const fetchAndFormatMessage = async () => {
    setIsLoadingMessage(true);
    try {
      // Buscar template de renovação (usado para copiar no painel e nos disparos)
      const templateResponse = await apiService.getRenewalTemplate();
      const template = templateResponse.data?.body || templateResponse.data?.content || '';
      
      if (template) {
        const message = formatMessage(template, client, renewalData);
        setFormattedMessage(message);
      } else {
        setFormattedMessage(generateDefaultMessage(client, renewalData));
      }
    } catch (error) {
      console.error('Erro ao buscar template de renovação:', error);
      setFormattedMessage(generateDefaultMessage(client, renewalData));
    } finally {
      setIsLoadingMessage(false);
    }
  };

  const formatMessage = (template: string, client: any, renewalData: any) => {
    let message = template;

    const userBold = whatsappCredentialBlock(client.username);
    const passBold = whatsappCredentialBlock(client.password);
    message = message.replace(/\{username\}/g, userBold);
    message = message.replace(/\{password\}/g, passBold);
    message = message.replace(/\{email\}/g, client.email || '');
    message = message.replace(/\{contact\}/g, client.whatsapp || '');
    message = message.replace(/\{whatsapp\}/g, client.whatsapp || '');

    // Datas
    message = message.replace(/\{expDate\}/g, renewalData.new_exp_date_formatted || '');
    message = message.replace(/\{expDateTime\}/g, new Date(renewalData.new_exp_date * 1000).toLocaleString('pt-BR'));
    message = message.replace(/\{expDateShort\}/g, new Date(renewalData.new_exp_date * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }));

    // Renovação: dias até expirar (opcional no template de renovação)
    message = message.replace(/\{daysToExpire\}/g, '—');

    // Sistema e conexões
    const systemName = client.system === 1 ? 'IPTV' : (client.system === 2 ? 'P2P' : 'P2P BR');
    message = message.replace(/\{system\}/g, systemName);
    const maxConnections = client.max_connections || client.maxConnections || 1;
    message = message.replace(/\{maxConnections\}/g, String(maxConnections));

    // Pacote
    const packageName = client.package_name || client.packageName || 'N/A';
    message = message.replace(/\{packageName\}/g, packageName);

    // Data/hora atual
    const now = new Date();
    message = message.replace(/\{currentDate\}/g, now.toLocaleDateString('pt-BR'));
    message = message.replace(/\{currentTime\}/g, now.toLocaleTimeString('pt-BR'));

    // Emojis
    message = message.replace(/\{emoji_alert\}/g, '⚠️');
    message = message.replace(/\{emoji_clock\}/g, '⏰');
    message = message.replace(/\{emoji_check\}/g, '✅');
    message = message.replace(/\{emoji_star\}/g, '⭐');
    message = message.replace(/\{emoji_calendar\}/g, '📅');
    message = message.replace(/\{emoji_phone\}/g, '📱');

    return message;
  };

  const generateDefaultMessage = (client: any, renewalData: any) => {
    const systemName = client.system === 1 ? 'IPTV' : (client.system === 2 ? 'P2P' : 'P2P BR');
    const maxConnections = client.max_connections || client.maxConnections || 1;
    const packageName = client.package_name || client.packageName || 'N/A';
    const userBlock = whatsappCredentialBlock(client.username);
    const passSection = client.password
      ? `\n🔑 *Senha:*\n${whatsappCredentialBlock(client.password)}\n`
      : '';

    return `✅ *Renovação Realizada com Sucesso!*

Olá!

Sua conta foi renovada com sucesso.

👤 *Usuário:*
${userBlock}${passSection}
📅 *Nova data de vencimento:* ${renewalData.new_exp_date_formatted}
📦 *Pacote:* ${packageName}
🔗 *Conexões:* ${maxConnections}
🖥️ *Sistema:* ${systemName}

Sua conta está ativa e funcionando normalmente!`;
  };

  const handleCopy = async () => {
    const text = formattedMessage?.trim() || '';
    if (!text) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, text.length);
        document.execCommand('copy');
        // Evita NotFoundError intermitente (o node pode já não estar no DOM)
        try {
          textarea.remove();
        } catch (_) {
          if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        }
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, text.length);
        document.execCommand('copy');
        // Evita NotFoundError intermitente (o node pode já não estar no DOM)
        try {
          textarea.remove();
        } catch (_) {
          if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy falhou:', fallbackErr);
      }
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const isTrial = client.is_trial === 1 || client.is_trial === true;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${
            isVisible ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div 
          className={`inline-block w-full max-w-2xl px-6 pt-6 pb-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg border-2 border-green-300 ${
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 rounded-full mr-3 bg-green-50">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                {isTrial ? 'Teste Renovado com Sucesso!' : 'Cliente Renovado com Sucesso!'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Informações da Renovação */}
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span className="font-medium text-gray-900">{client.username}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Nova data de vencimento:</span>
                <span className="font-medium text-gray-900">{renewalData.new_exp_date_formatted}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Custo pago:</span>
                <span className="font-medium text-gray-900">{renewalData.cost_paid} crédito{renewalData.cost_paid !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          {/* Mensagem Formatada */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <MessageSquare className="w-4 h-4 mr-2" />
                Mensagem para o Cliente
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                disabled={isLoadingMessage || !formattedMessage?.trim()}
                className="flex items-center"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Mensagem
                  </>
                )}
              </Button>
            </div>
            
            {isLoadingMessage ? (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                <div className="inline-block w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <p className="mt-2 text-sm text-gray-600">Carregando mensagem...</p>
              </div>
            ) : (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                  {formattedMessage}
                </pre>
              </div>
            )}
          </div>

          {/* Aviso */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Mensagem copiada!</p>
                <p>Você pode enviar esta mensagem manualmente ao cliente através do WhatsApp ou outro canal de comunicação.</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end mt-6">
            <Button
              type="button"
              onClick={handleClose}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalSuccessModal;
